import { ActionHandler } from '@components/MultipleActionsInterfaces';
import { useWorkspaceMassActions } from '@components/v2/layout/AppTemplate/hooks/useWorkspaceMassActions';
import { WorkspaceMassActionsConfig } from '@components/v2/layout/MassActionsBar/massActionsBar.types';
import { useEffect } from 'react';
import useMassActions from '../../../../../hooks/useMassActions/useMassActions';
import { useModalState } from '../../../../../hooks/useModalState';
import { getOrderProductUniqueIdentifier } from '../../../../../utils/getOrderProductUniqueIdentifier';
import { OrderProductsTestIds } from '../../../../../utils/testIds/OrderProducts.testIds';
import OrderProductsMassActionsSummary from '../../components/OrderProductsMassActionsSummary/OrderProductsMassActionsSummary';
import {
  OrderProductsMassActionType,
  buildOrderProductsMassActions,
} from '../../utils/orderProductsMassActions.utils';
import { UseOrderProductsMassActionsParams } from './useOrderProductsMassActions.types';
import useOrderProductsMassActionsSummary from './useOrderProductsMassActionsSummary';

const useOrderProductsMassActions = ({
  products,
  totalOrderProductsCount,
  isSelectionEnabled,
  conditionFilterKey,
}: UseOrderProductsMassActionsParams) => {
  const copyOrderProductsToNewOrderModal = useModalState();
  const copyOrderProductsToExistingOrderModal = useModalState();
  const removeOrderProductsFromOrderModal = useModalState();

  const massActionsManager = useMassActions<string>();
  const { selectedItems, clearSelectedItems } = massActionsManager;

  useEffect(() => {
    clearSelectedItems();
  }, [conditionFilterKey, clearSelectedItems]);

  const totalSelected = selectedItems.size;
  const isAllItemsSelected = totalSelected === totalOrderProductsCount;

  const listedIdentifiers = products.map((product) => getOrderProductUniqueIdentifier(product));
  const isAllListedSelected =
    listedIdentifiers.length > 0 &&
    listedIdentifiers.every((identifier) => selectedItems.has(identifier));

  const summaryTotals = useOrderProductsMassActionsSummary({
    selectedItems,
    isAllSelected: isAllItemsSelected,
  });

  const toggleAllSelected = () => {
    if (isAllListedSelected) {
      massActionsManager.removeItems(new Set(listedIdentifiers));
      return;
    }

    massActionsManager.selectItems(new Set(listedIdentifiers));
  };

  const handleClickMassAction: ActionHandler<OrderProductsMassActionType> = (action) => {
    switch (action.type) {
      case 'copy_order_products_to_new_order':
        copyOrderProductsToNewOrderModal.openModal();
        break;
      case 'copy_order_products_to_existing_order':
        copyOrderProductsToExistingOrderModal.openModal();
        break;
      case 'remove_order_products_from_order':
        removeOrderProductsFromOrderModal.openModal();
        break;
      default:
        break;
    }
  };

  const config: WorkspaceMassActionsConfig<OrderProductsMassActionType> =
    isSelectionEnabled && totalSelected > 0
      ? {
          totalSelected,
          isAllSelected: isAllItemsSelected,
          actions: buildOrderProductsMassActions(),
          onClickAction: handleClickMassAction,
          toggleAllSelected,
          deselectAll: massActionsManager.clearSelectedItems,
          massActionFilter: {
            enabled: true,
            value: isAllListedSelected,
            totalResults: listedIdentifiers.length,
            onToggle: toggleAllSelected,
          },
          testId: OrderProductsTestIds.MASS_ACTIONS_BAR,
          slots: {
            trailing: (
              <OrderProductsMassActionsSummary
                totalItems={totalSelected}
                totalPieces={summaryTotals.totalPieces}
                totalValue={summaryTotals.totalValue}
              />
            ),
          },
        }
      : null;

  useWorkspaceMassActions<OrderProductsMassActionType>(config);

  const onClose = (shouldResetMassActions?: boolean, callback?: () => void) => {
    if (callback) {
      callback();
    }
    if (shouldResetMassActions) {
      massActionsManager.reset();
    }
  };

  return {
    ...massActionsManager,
    isAllItemsSelected,
    copyOrderProductsToNewOrderModal,
    copyOrderProductsToExistingOrderModal,
    removeOrderProductsFromOrderModal,
    onClose,
  };
};

export default useOrderProductsMassActions;
